import React, { useContext, useEffect, useState } from 'react';
import {
  ApiTypesEnum,
  LovWithoutIdDTO,
  SchenkerAddonTypeSelectorEnum,
  SchenkerLovForAddonTypeOutDTO,
} from '@api/ringil-api.v2';
import { Carrier } from '@components/RingilComponents/CarrierDistributionSelector/CarrierDistributionSelector.tsx';
import { ApiContext } from '@api/api.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { FieldLabel } from '@components/framework/formfield/FieldLabel.tsx';
import InputSelect from '@components/ringil3/Input/InputSelect/InputSelect.tsx';
import { InputCheckbox } from '@components/ringil3/Input/InputCheckbox/InputCheckbox.tsx';

interface ComponentProps {
  values: Carrier[];
  onSelect: (values: Carrier[]) => void;
}

export const SchenkerProductTerms: React.FC<ComponentProps> = ({
                                                                 values,
                                                                 onSelect,
                                                               }) => {
  const { tr } = useTranslationLgs();
  const [productTypes, setProductTypes] = useState<LovWithoutIdDTO[]>([]);
  const { lovSchenkerProductTypes, lovSchenkerAddons } = useContext(ApiContext);
  const [schenkerAddons, setSchenkeraddons] = useState<SchenkerLovForAddonTypeOutDTO[]>([]);
  const [isSchenker, setIsShenker] = useState<boolean>(false);
  const [schenkerCarriers, setSchenkerCarriers] = useState<Carrier[]>([]);
  const [queriedType, setQueriedType] = useState<string>(null);

  useEffect(() => {
    const hasSchenkerApi = Array.isArray(values) && values.some(carrier =>
      Array.isArray(carrier.apiTypes) && carrier.apiTypes.includes(ApiTypesEnum.SCHENKER),
    );

    setIsShenker(hasSchenkerApi);

    const shenkerCarriers = Array.isArray(values)
      ? values.filter(carrier =>
        Array.isArray(carrier.apiTypes) && carrier.apiTypes.includes(ApiTypesEnum.SCHENKER),
      )
      : [];
    setSchenkerCarriers(shenkerCarriers);

    if (hasSchenkerApi && (!productTypes || productTypes.length === 0)) {
      lovSchenkerProductTypes(values => {
        setProductTypes(values);
      });
    }
  }, [values]);

  const refreshSchenkerAddons = (productTypeCode: string) => {
    if (queriedType !== productTypeCode) {
      lovSchenkerAddons(productTypeCode || productTypes[0].code, values => {
        setSchenkeraddons(values);
        setQueriedType(productTypeCode);
      });
    }
  }

  useEffect(() => {
    if (schenkerCarriers && schenkerCarriers.length > 0 && productTypes?.length > 0) {
      refreshSchenkerAddons(schenkerCarriers[0]._schenkerProductTypeCode);

      const needsUpdate = schenkerCarriers.some(
        carrier => !carrier._schenkerProductTypeCode,
      );

      if (needsUpdate) {
        const updated = schenkerCarriers.map(carrier => ({
          ...carrier,
          _schenkerProductTypeCode: carrier._schenkerProductTypeCode || schenkerCarriers[0]._schenkerProductTypeCode || productTypes[0].code,
          _schenkerAddonsTypeCodes: carrier._schenkerAddonsTypeCodes || schenkerCarriers[0]._schenkerAddonsTypeCodes,
        }));

        onSelect(updated);
      }
    }
  }, [schenkerCarriers, productTypes]);

  const handleApiProductSelect = (_schenkerProductTypeCode: string) => {
    const updatedValue = schenkerCarriers.map(item => {
      return {
        ...item,
        _schenkerProductTypeCode,
        _schenkerAddonsTypeCodes: null,
      };
    });

    onSelect(updatedValue);
  };

  const handleApiAddonsSelect = (codesToAdd: string[], modifiedCodes: string[]) => {
    const updatedValue = schenkerCarriers.map(item => {
      const filteredCodes = (item._schenkerAddonsTypeCodes || []).filter(
        code => !modifiedCodes.includes(code),
      );

      return {
        ...item,
        _schenkerAddonsTypeCodes: [...filteredCodes, ...codesToAdd],
      };
    });

    onSelect(updatedValue);
  };

  return (
    <>
      {isSchenker && (
        <div className="p-4 rounded-lg bg-base-tertiary gap-4 flex flex-col">
          <Field
            label={tr('SchenkerProductTerms.schenkerProduct', 'Schenker - select product')}
            required
          >
            <Dropdown
              options={productTypes?.map(pt => ({
                label: tr(`carrier.api.schenker.product.${pt.code}`, pt.name),
                value: pt.code
              }))}
              optionLabel="label"
              optionValue="value"
              value={schenkerCarriers[0]._schenkerProductTypeCode || (productTypes && productTypes[0]?.code)}
              onChange={e => handleApiProductSelect(e.value)}
              style={{ width: '100%' }}
              placeholder={tr('SchenkerProductTerms.schenkerProductPlaceholder', 'Select product')}
            />
          </Field>

          <div className="flex flex-col gap-4">
            {schenkerAddons
              .sort((a, b) => a.group - b.group)
              .filter(addon => addon.values && addon.values.length > 0)
              .map(addon => {
                switch (addon.schenkerAddonTypeSelector) {
                  case SchenkerAddonTypeSelectorEnum.RADIO_BOX:
                    return (
                      <div>
                        <FieldLabel
                          label={tr('carrier.api.schenker.group.' + addon.group, 'Group')}
                        />
                        <InputSelect
                          model={addon.values.map(value => ({
                            code: value.code,
                            label: tr('carrier.api.schenker.addon.' + value.code, value.name),
                          }))}
                          selectedItemCode={
                            schenkerCarriers[0]._schenkerAddonsTypeCodes?.find(code =>
                              addon.values.some(value => value.code === code),
                            ) ?? null
                          }
                          onSelect={value => handleApiAddonsSelect([value], addon.values.map(it => it.code))}
                        />
                      </div>
                    );
                  case SchenkerAddonTypeSelectorEnum.CHECK_BOX:
                    return (
                      <div className="mt-1">
                          <InputCheckbox
                            value={schenkerCarriers[0]._schenkerAddonsTypeCodes?.some(code => addon.values.some(value => value.code === code))}
                            onChange={(_: string, checked: boolean) => {
                              handleApiAddonsSelect(checked ? addon.values.map(it => it.code) : [], addon.values.map(it => it.code));
                            }}
                            label={tr('carrier.api.schenker.addon.' + addon.values[0].code, addon.values[0].name)}
                          />

                      </div>
                    );
                }
              })}
          </div>
        </div>
      )}
    </>
  );
};